import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called once the dialog has finished closing (after it animates away). */
  onClosed?: () => void;
  /**
   * The element it opens from and closes back into (a post's board tile), if any: the dialog grows
   * out of it and shrinks back into it.
   */
  origin?: () => Element | null;
  labelledBy: string;
  className?: string;
  children: ReactNode;
}

const reducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/** The transform that puts `el` (laid out at `box`) over `target`, scaling from its top left. */
function over(target: Element, box: DOMRect): string | null {
  const r = target.getBoundingClientRect();
  if (!r.width || !r.height || !box.width || !box.height) return null;
  return `translate(${r.left - box.left}px, ${r.top - box.top}px) scale(${r.width / box.width}, ${r.height / box.height})`;
}

/** Opening: grow out of the origin, taking on its colour, or else rise in. */
function grow(d: HTMLDialogElement, origin: Element | null) {
  if (reducedMotion()) return;
  const from = origin && over(origin, d.getBoundingClientRect());
  if (from && origin) {
    const timing = { duration: 420, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' };
    d.animate(
      [
        {
          transformOrigin: '0 0',
          transform: from,
          backgroundColor: getComputedStyle(origin).backgroundColor,
        },
        {
          transformOrigin: '0 0',
          transform: 'none',
          backgroundColor: getComputedStyle(d).backgroundColor,
        },
      ],
      timing,
    );
    d.firstElementChild?.animate(
      [{ opacity: 0 }, { opacity: 0, offset: 0.4 }, { opacity: 1 }],
      timing,
    );
  } else {
    d.animate(
      [
        { opacity: 0, transform: 'translateY(10px) scale(0.98)' },
        { opacity: 1, transform: 'none' },
      ],
      { duration: 200, easing: 'ease-out' },
    );
  }
}

/**
 * Closing: shrink back into the origin if it's still on the board, turning its colour and fading
 * as it lands, or else fade away.
 */
function shrink(d: HTMLDialogElement, origin: Element | null): Promise<unknown> {
  if (reducedMotion()) return Promise.resolve();
  d.classList.add('is-closing'); // fades the backdrop (styles.css)
  const to = origin && over(origin, d.getBoundingClientRect());
  const timing = { duration: to ? 340 : 160, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' };
  const anim =
    to && origin
      ? d.animate(
          [
            { transformOrigin: '0 0', transform: 'none', opacity: 1 },
            { opacity: 1, offset: 0.7 },
            {
              transformOrigin: '0 0',
              transform: to,
              backgroundColor: getComputedStyle(origin).backgroundColor,
              opacity: 0,
            },
          ],
          timing,
        )
      : d.animate([{ opacity: 1 }, { opacity: 0, transform: 'scale(0.98)' }], timing);
  d.firstElementChild?.animate(
    [{ opacity: 1 }, { opacity: 0, offset: 0.4 }, { opacity: 0 }],
    timing,
  );
  return anim.finished.catch(() => undefined);
}

/** Native modal <dialog>: focus trapping, Esc and the backdrop come from the browser. */
export function Dialog({
  open,
  onClose,
  onClosed,
  origin,
  labelledBy,
  className,
  children,
}: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  // The content stays while the dialog animates away.
  const [shown, setShown] = useState(open);
  if (open && !shown) setShown(true);
  const latest = useRef({ open, origin, onClosed });
  useLayoutEffect(() => {
    latest.current = { open, origin, onClosed };
  });

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      d.classList.remove('is-closing');
      d.showModal();
      grow(d, latest.current.origin?.() ?? null);
    }
    if (!open && d.open && !d.classList.contains('is-closing')) {
      void shrink(d, latest.current.origin?.() ?? null).then(() => {
        d.classList.remove('is-closing');
        if (latest.current.open) return; // opened again meanwhile (Back, then Forward)
        d.close();
        setShown(false);
        latest.current.onClosed?.();
      });
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={`dialog ${className ?? ''}`}
      aria-labelledby={labelledBy}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose(); // click on the backdrop
      }}
    >
      {(open || shown) && (
        <div className="dialog__inner">
          <button type="button" className="dialog__close" aria-label="Close" onClick={onClose}>
            ×
          </button>
          {children}
        </div>
      )}
    </dialog>
  );
}
